import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import styles from "./css/CityInfoTab.module.css";
import { GraphsCard } from "@/components/cards/GraphsCard";
import { GaugeChart } from "@/components/graphs/GaugeChart";
import { CityAttributes, Favorites, ConsumptionData } from "@/types";
import { PieGraph } from "@/components/graphs/PieGraph";
import { SimpleYearComparisonChart } from "@/components/graphs/SimpleYearComparisonGraph";
import { GroupedYearComparisonChart } from "@/components/graphs/GroupedYearComparisonChart";
import { ChargingPowerGraphCity } from "@/components/graphs//ChargingPowerGraphCity";
import { ChargingBarsGraphCity } from "@/components/graphs/ChargingBarsGraphCity";
import { GaugeChartEnergyCreation } from "@/components/graphs/GaugeChartEnergyCreation";

interface FavoriteTabProps {
  cityAttributes: CityAttributes | null;
  consumptionData: ConsumptionData | null;
  favorites: Favorites;
  setFavorites: (favorites: Favorites) => void;
  cityAttributesColors: {
    usage: {
      [key: string]: string;
    };
    residents: {
      [key: string]: string;
    };
    vehicles: {
      [key: string]: string;
    };
    apartments: {
      [key: string]: string;
    };
    gas_emissions: {
      [key: string]: string;
    };  
    electric_vehicles: {
      [key: string]: string;
    };
    ev_charging_stations: {
      [key: string]: string;
    };
    charging_providers: {
      [key: string]: string;
    };
    energy_consumption: { [key: string]: string };
  };
}
export const FavoriteTab: React.FC<FavoriteTabProps> = ({ cityAttributes, favorites, setFavorites, cityAttributesColors, consumptionData }) => {
  const { t } = useTranslation('CityInfoTab');

  // Dynamically extract years from data
  const energyYears = cityAttributes?.energy_consumption_dict ? Object.keys(cityAttributes.energy_consumption_dict).sort() : [];
  const currentYear = energyYears[0] || "2025";
  const futureYear = energyYears[1] || "2040";

  const setFavourite = (id: string, isFavourite: boolean) => {
    setFavorites({ ...favorites, [id]: isFavourite });
  };


  return (
    <div className={styles.container}>
      {favorites.residents && <GraphsCard title={t('residents')} children={<SimpleYearComparisonChart data={cityAttributes?.population_count_dict} />} setFavourite={setFavourite} isFavourite={favorites.residents} id="residents"/>}
      {favorites.vehicles && <GraphsCard title={t('vehicles')} children={<SimpleYearComparisonChart data={cityAttributes?.vehicles_count_dict} />} setFavourite={setFavourite} isFavourite={favorites.vehicles} id="vehicles"/>}
      {favorites.apartments && <GraphsCard 
        title={t('residentialUnits')}
        children={<SimpleYearComparisonChart data={cityAttributes?.residential_units_count_dict} />} 
        setFavourite={setFavourite} 
        isFavourite={favorites?.apartments ?? false} 
        id="apartments"
      />}
      {favorites.usage && <GraphsCard title={t('metersByUsage')} children={<GroupedYearComparisonChart data={cityAttributes?.areas_dict} />} setFavourite={setFavourite} isFavourite={favorites.usage} id="usage"/>}
      {favorites.greenhouseEmissions && <GraphsCard title={t('totalGhgEmissions')} children={<SimpleYearComparisonChart data={cityAttributes?.gas_emissions} />} setFavourite={setFavourite} isFavourite={favorites.greenhouseEmissions} id="greenhouseEmissions"/>}
      {favorites.numberOfElectricVehicles && <GraphsCard title={t('electricVehicles')} children={<SimpleYearComparisonChart data={cityAttributes?.electric_vehicles_count_dict} />} setFavourite={setFavourite} isFavourite={favorites.numberOfElectricVehicles} id="numberOfElectricVehicles"/>}
      {favorites.chargingStations && <GraphsCard 
              title={t('chargingStations')}
              children={
                  <ChargingBarsGraphCity 
                      data={cityAttributes?.ev_charging_stations_count} 
                      size="normal"
                  />
              } 
              id="chargingStations"
              setFavourite={setFavourite}
              isFavourite={favorites?.chargingStations ?? false}
            />}
      {favorites.existingCapacity && <GraphsCard 
              title={t('chargingProviders')}
              children={<ChargingPowerGraphCity data={cityAttributes?.charging_providers} />} 
              setFavourite={setFavourite} 
              isFavourite={favorites?.existingCapacity ?? false} 
              id="existingCapacity"
            />}
      {favorites.energy && <GraphsCard  title={t('energyResilience')} children={<GaugeChart
                data={cityAttributes?.energy_resilience?.[futureYear]?.max}
                needle_value={cityAttributes?.energy_resilience?.[futureYear]?.needle ?? 0}
                second_needle_value={cityAttributes?.energy_resilience?.[futureYear]?.second_needle ?? 0}
                year={futureYear}
              />} setFavourite={setFavourite} isFavourite={favorites.energy} id="energy"/>}
      {favorites.energyCreation && <GraphsCard 
        title={t('renewableEnergyGeneration')}
        children={<GaugeChartEnergyCreation
          data={cityAttributes?.renewable_energy_generation?.potential_production}
          needle_value={cityAttributes?.renewable_energy_generation?.existing_production ?? 0}
          year="מגה וואט"
        />}
        setFavourite={setFavourite} 
        isFavourite={favorites?.energyCreation ?? false} 
        id="energyCreation"
      />}
      {favorites.energyConsumption && <GraphsCard isFavourite={favorites.energyConsumption}  title={t('energyConsumption', { year: currentYear })} children={<PieGraph data={cityAttributes?.energy_consumption_dict[currentYear]} colors={cityAttributesColors.energy_consumption as { [key: string]: string }}  showLegend={true} />} setFavourite={setFavourite} id="energyConsumption" />}
      {favorites.energyConsumption_future && <GraphsCard isFavourite={favorites.energyConsumption_future}  title={t('energyConsumption', { year: futureYear })} children={<PieGraph data={cityAttributes?.energy_consumption_dict[futureYear]} colors={cityAttributesColors.energy_consumption as { [key: string]: string }}  showLegend={true} />} setFavourite={setFavourite} id="energyConsumption_future" />}

    </div>  
  );
};
